import React, {memo} from 'react';
import {useSelector} from 'react-redux';
import cn from 'classnames';
import {LocationWithWeather} from '@/app/interfaces/weather';
import {YearPeriod} from '@/common/interfaces/date';
import {RouteName} from '@/app/constants/routing';
import {useLocalizer} from '@/common/services/localizer';
import {useUrlBuilder} from '@/app/routing/useUrlBuilder';
import {temperatureDisplayReduce} from '@/common/helpers/unitReducers/temperatureDisplayReduce';
import {selectTemperatureUnit} from '@/app/store/config/selectors';
import {RouterLink} from '@/common/components/RouterLink';
import {ConditionMediumIcon} from '@/common/components/ConditionMediumIcon';
import {TextLink} from '@/common/components/TextLink';
import styles from './WeatherResorts.css';

const yearPeriodStyles: Record<YearPeriod, string> = {
	winter: styles.isWinter,
	spring: styles.isSummer,
	summer: styles.isSummer,
	autumn: styles.isSummer,
};

interface WeatherResortsProps {
	period: YearPeriod;
	locations: LocationWithWeather[];
}

export const WeatherResorts = memo<WeatherResortsProps>(props => {
	const {period, locations} = props;

	const temperatureUnit = useSelector(selectTemperatureUnit);

	const urlBuilder = useUrlBuilder();

	const t = useLocalizer();

	if (!locations.length) {
		return null;
	}

	return (
		<div className={cn(styles.root, yearPeriodStyles[period])}>
			<h2 className={styles.title}>{t(`resortsWeatherTitle.${period}`)}</h2>
			<ul>
				{locations.map(({location, weather}) => (
					<li key={location.id} className={styles.entry}>
						<div className={styles.mainRow}>
							<RouterLink
								className={styles.link}
								component={TextLink}
								url={urlBuilder(RouteName.weather, {location})}>
								{t('weather')} {location.title}
							</RouterLink>
							<div className={styles.fillRow} />
							{weather && (
								<>
									<p className={styles.temperature}>
										{temperatureDisplayReduce(weather.temperature, temperatureUnit)}
									</p>

									<ConditionMediumIcon
										className={styles.conditionIcon}
										condition={weather.condition}
										isNight={weather.isNight}
									/>
								</>
							)}
						</div>

						{weather?.waterTemperature !== undefined && (
							<p className={styles.waterTemperature}>
								{t('waterTemp')} {temperatureDisplayReduce(weather.waterTemperature, temperatureUnit)}
							</p>
						)}
					</li>
				))}
			</ul>
		</div>
	);
});

WeatherResorts.displayName = 'WeatherResorts';
